import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { getInitials } from '@/lib/format'
import { usePresence, type PresencePerson } from '@/lib/realtime'
import { useSession } from '@/lib/session'
import { cn } from '@/lib/utils'

/**
 * Quem está com o Supply aberto agora, ao lado do ponto "Ao vivo": avatares
 * sobrepostos, no máximo 4 (2 no celular) e um "+N" com o resto.
 *
 * Você entra na pilha, sempre na última vaga e com anel: sem isso, "só você online" e
 * "ninguém online / presença quebrada" ficam iguais. Os outros vêm primeiro,
 * porque é deles que a pilha informa. A presença vem do canal `supply:ao-vivo`
 * (`lib/realtime.tsx`); sem tempo real (desligado ou caiu) a pilha some e o
 * ponto ao lado já diz o motivo — o topo nunca quebra.
 */

const MAX_DESKTOP = 4
const MAX_MOBILE = 2

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name
}

export function PresenceStack() {
  const { status, people } = usePresence()
  const { member } = useSession()

  if (status === 'conectando') {
    return (
      <span role="status" aria-label="Carregando quem está online" className="flex -space-x-2">
        <Skeleton className="h-7 w-7 rounded-full border-2 border-background" />
        <Skeleton className="hidden h-7 w-7 rounded-full border-2 border-background sm:block" />
      </span>
    )
  }
  if (status !== 'ao_vivo' || people.length === 0) return null

  const others = people.filter((p) => p.memberId !== member.id)
  const me = people.find((p) => p.memberId === member.id)
  // Você ocupa sempre a última vaga visível; o "+N" conta só os outros.
  const mobileSlots = me ? MAX_MOBILE - 1 : MAX_MOBILE
  const desktopSlots = me ? MAX_DESKTOP - 1 : MAX_DESKTOP
  const total = others.length + (me ? 1 : 0)
  const label =
    others.length === 0
      ? 'Só você está online agora'
      : `${total} pessoas online: ${[...others.map((p) => firstName(p.name)), ...(me ? ['você'] : [])].join(', ')}`

  return (
    <div role="group" aria-label={label} className="flex items-center -space-x-2">
      {others.map((person, index) => (
        <PresenceAvatar
          key={person.memberId}
          person={person}
          isMe={false}
          alone={false}
          className={cn(index >= mobileSlots && 'hidden sm:flex', index >= desktopSlots && 'sm:hidden')}
        />
      ))}
      <Overflow people={others.slice(mobileSlots)} className="sm:hidden" />
      <Overflow people={others.slice(desktopSlots)} className="hidden sm:flex" />
      {me && <PresenceAvatar person={me} isMe alone={others.length === 0} />}
    </div>
  )
}

function PresenceAvatar({
  person,
  isMe,
  alone,
  className,
}: {
  person: PresencePerson
  isMe: boolean
  alone: boolean
  className?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={isMe ? `${person.name} (você)` : person.name}
          className={cn(
            'flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <Avatar
            className={cn(
              'h-7 w-7 rounded-full border-2 border-background',
              isMe && 'ring-2 ring-primary/60 ring-offset-0',
            )}
          >
            {person.avatarUrl && <AvatarImage src={person.avatarUrl} alt="" className="rounded-full object-cover" />}
            <AvatarFallback className="rounded-full text-[10px]">{getInitials(person.name)}</AvatarFallback>
          </Avatar>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {isMe ? (alone ? 'Só você está online agora' : `${person.name} (você)`) : `${person.name} · online agora`}
      </TooltipContent>
    </Tooltip>
  )
}

function Overflow({ people, className }: { people: readonly PresencePerson[]; className?: string }) {
  if (people.length === 0) return null
  const names = people.map((p) => p.name)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={`Mais ${people.length}: ${names.join(', ')}`}
          className={cn(
            'flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-background bg-muted px-1 font-mono text-[10px] font-semibold tabular-nums text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          +{people.length}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-60">
        <ul className="space-y-0.5">
          {names.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}
